<template>
  <div class="min-h-screen bg-[#f9fafb] flex flex-col">
    <!-- Header -->
    <div class="flex gap-2 w-full items-center p-4">
      <button
        class="text-gray-700 text-xl"
        @click="goBack"
      >
        <i class="fas fa-arrow-left" />
      </button>
      <div class="flex-1 mt-5">
        <div class="w-full bg-gray-200 h-2 rounded-full">
          <div
            class="h-2 bg-blue-500 rounded-full"
            :style="{ width: pageProgress + '%' }"
          />
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Reviewing {{ endIndex }} / {{ totalQuestions }}
        </p>
      </div>
      <div class="flex items-center text-red-500 font-semibold">
        <i class="fas fa-heart mr-1" /> <span>5</span>
      </div>
    </div>

    <!-- Review list (paged by 5) -->
    <div class="px-4 mt-6 flex-1 flex flex-col gap-6">
      <div
        v-if="fetchError"
        class="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl"
      >
        {{ fetchError }}
      </div>
      <div
        v-if="isLoading && !fetchError && !review.questions.length"
        class="flex justify-center py-10"
      >
        <i class="fas fa-spinner fa-spin text-blue-600 text-xl" />
      </div>
      <div
        v-for="q in pagedQuestions"
        :key="q.questionId"
        class="bg-white rounded-xl shadow p-6"
      >
        <div class="flex items-start justify-between gap-3 mb-3">
          <h3
            class="text-[16px] text-gray-900 leading-relaxed"
            v-html="q.question"
          />
          <!-- time chip (static demo 20s like mock) -->
          <!-- <span
            class="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap flex items-center gap-1"
          >
            <i class="far fa-clock" /> 20 Sec
          </span> -->
        </div>

        <div
          v-if="q.image"
          class="flex justify-center mb-4"
        >
          <img
            :src="q.image"
            alt="question image"
            class="max-h-48 object-contain rounded"
          >
        </div>

        <!-- Options (read-only) -->
        <div class="flex flex-col gap-3">
          <div
            v-for="opt in q.options"
            :key="`${q.questionId}-${opt.id}`"
            :class="optionRowClass(q, opt)"
          >
            <!-- bullet -->
            <span
              class="answer-bullet border"
              :class="bulletClass(q, opt)"
            >
              <i
                v-if="isSelected(q, opt) && isCorrectChoice(q, opt)"
                class="fas fa-check text-[10px]"
              />
            </span>

            <span class="block">{{ opt.text }}</span>

            <!-- status pill on the right -->
            <div
              v-if="isSelected(q, opt) && !isCorrectChoice(q, opt)"
              class="ml-auto flex items-center gap-2 text-sm"
            >
              <i class="fas fa-times-circle" />
              <span>Wrong answer</span>
            </div>
            <div
              v-else-if="isCorrectChoice(q, opt)"
              class="ml-auto flex items-center gap-2 text-sm"
            >
              <i class="fas fa-check-circle" />
              <span>Correct</span>
            </div>
          </div>
        </div>
        <CautionDrawer />
      </div>
    </div>

    <!-- Bottom Nav -->
    <div
      class="px-4 py-4 border-t bg-white mt-6 flex items-center justify-between"
    >
      <button
        class="text-sm font-semibold text-gray-600 px-4 py-2 rounded-full border border-gray-300"
        :disabled="page === 0 || isLoading"
        :class="{
          'opacity-50 cursor-not-allowed': page === 0 || isLoading,
        }"
        @click="prevPage"
      >
        Previous
      </button>

      <button
        class="bg-blue-600 text-white rounded-full px-8 py-3 text-sm font-semibold"
        :disabled="isLoading"
        :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
        @click="nextOrFinish"
      >
        {{ isLastPage ? "Finish" : "Next" }}
      </button>
    </div>

    <Transition name="modal">
      <div
        v-if="showPinPrompt"
        class="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      >
        <div
          class="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 relative"
        >
          <button
            class="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            @click="closePinPrompt"
          >
            <i class="fas fa-times text-gray-500" />
          </button>

          <div class="text-center">
            <h4 class="text-lg font-semibold text-gray-900">
              Enter Review PIN
            </h4>
            <p class="text-gray-600 text-sm mt-2">
              This quiz review is protected. Please enter the 4-digit PIN to
              continue.
            </p>
          </div>

          <div class="flex justify-center mt-6">
            <PinInput v-model="reviewKey" />
          </div>

          <p
            v-if="pinError"
            class="text-sm text-red-500 text-center mt-3"
          >
            {{ pinError }}
          </p>

          <div class="space-y-3 mt-6">
            <button
              class="w-full bg-blue-600 text-white py-3 rounded-full text-base font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="reviewKey.length !== 4 || isLoading"
              @click="onSubmitPin"
            >
              <span
                v-if="isLoading"
                class="flex items-center justify-center gap-2"
              >
                <i class="fas fa-spinner fa-spin" />
                Validating...
              </span>
              <span v-else>Submit PIN</span>
            </button>
            <button
              class="w-full border border-blue-600 text-blue-600 py-3 rounded-full text-base font-semibold hover:bg-blue-50"
              @click="closePinPrompt"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { GetQuizReview, VerifyQuizReviewPin } from "@/api/quizApi";
import CautionDrawer from "@/components/CautionDrawer.vue";
import PinInput from "@/components/base/PinInput.vue";
import { useReviewBookmarkStore } from "@/stores/reviewBookmarkStore";

// ---- routing ----
const route = useRoute();
const router = useRouter();

// Optional params if you want to fetch by GUID or startId
// const guid = route.query.guid || route.params.guid || null
const startId = route.query.startId || route.params.startId || null;
const sanitizePin = (value = "") => value.replace(/\D/g, "").slice(0, 4);
const initialKey = sanitizePin(
  (route.query.key || route.query.Key || route.query.pin || "").toString()
);
const quizGuidRaw = route.query.quizGuid || route.query.guid || null;
const quizGuid = quizGuidRaw ? String(quizGuidRaw) : null;
const initialRequireReviewPin = ["1", "true", "yes"].includes(
  String(route.query.requireReviewPin ?? "").toLowerCase()
);

// ---- state ----
const review = ref({ totalQuestions: 0, questions: [] });
const isLoading = ref(false);
const fetchError = ref("");
const showPinPrompt = ref(initialRequireReviewPin && !initialKey);
const reviewKey = ref(initialKey);
const pinError = ref("");
const requireReviewPin = ref(initialRequireReviewPin);
const page = ref(0);
const PAGE_SIZE = 5;

const bookmarkStore = useReviewBookmarkStore();
bookmarkStore.hydrate();

// ---- computed ----
const totalQuestions = computed(() => review.value?.totalQuestions ?? 0);
const startIndex = computed(() => page.value * PAGE_SIZE);
const endIndex = computed(() =>
  Math.min(startIndex.value + PAGE_SIZE, totalQuestions.value)
);
const isLastPage = computed(() => endIndex.value >= totalQuestions.value);

const pageProgress = computed(() => {
  if (!totalQuestions.value) return 0;
  return Math.round((endIndex.value / totalQuestions.value) * 100);
});

const normalizedStartId = computed(() => {
  if (startId) return String(startId);
  const paramId = route.params.startId;
  return paramId ? String(paramId) : null;
});
const isBookmarked = computed(() =>
  normalizedStartId.value
    ? bookmarkStore.isBookmarked(normalizedStartId.value)
    : false
);
const correctCount = computed(() => {
  const list = review.value?.questions || [];
  return list.filter((q) => q.isCorrect).length;
});
const wrongCount = computed(() => {
  const list = review.value?.questions || [];
  return list.filter((q) => q.isCorrect === false).length;
});
const bookmarkTitle = computed(() => {
  const fallbackId = normalizedStartId.value
    ? `Quiz Review #${normalizedStartId.value}`
    : "Quiz Review";
  return (
    review.value?.quizTitle ||
    route.query.quizTitle ||
    route.query.title ||
    fallbackId
  );
});

const pagedQuestions = computed(() => {
  const list = review.value?.questions || [];
  return list.slice(startIndex.value, endIndex.value);
});

const parseVerificationResponse = (payload) => {
  if (!payload) return {};
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return { raw: payload };
    }
  }
  return payload;
};

// ---- helpers to compute styles ----
function isSelected(q, opt) {
  return q.selectedChoice === opt.id;
}
function isCorrectChoice(q, opt) {
  return q.correctChoice === opt.id;
}

function optionRowClass(q, opt) {
  const base =
    "w-full text-left px-4 py-3 rounded-lg border transition font-medium flex items-center gap-3";
  const selected = isSelected(q, opt);
  const correct = isCorrectChoice(q, opt);

  // Correct choice is always green in review
  if (correct) return `${base} border-green-500 bg-green-50 text-green-700`;

  // Wrong selected choice is red
  if (selected && !correct)
    return `${base} border-red-400 bg-red-50 text-red-700`;

  // Neutral others
  return `${base} border-gray-300 bg-white text-gray-800`;
}

function bulletClass(q, opt) {
  const selected = isSelected(q, opt);
  const correct = isCorrectChoice(q, opt);
  if (correct)
    return "answer-bullet--on border-green-600 bg-green-600 text-white";
  if (selected && !correct) return "border-red-500";
  return "border-gray-400 bg-white";
}

// ---- navigation ----
function prevPage() {
  if (page.value > 0) page.value--;
}
function nextOrFinish() {
  if (isLastPage.value) {
    // adapt the route name/path to your app
    router.back();
  } else {
    page.value++;
  }
}
function goBack() {
  router.back();
}

// ---- data fetching ----
function buildBookmarkPayload() {
  if (!normalizedStartId.value) return null;

  const query = {};
  const wrongOnlyRaw = route.query.wrongOnly;
  if (typeof wrongOnlyRaw !== "undefined") {
    query.wrongOnly = wrongOnlyRaw;
  }
  if (requireReviewPin.value) {
    query.requireReviewPin = "1";
  }
  const quizGuidForBookmark =
    review.value?.quizGuid ||
    quizGuid ||
    route.query.quizGuid ||
    route.query.guid ||
    null;
  if (quizGuidForBookmark) {
    query.quizGuid = quizGuidForBookmark;
  }
  if (bookmarkTitle.value) {
    query.quizTitle = bookmarkTitle.value;
  }

  const sanitizedQuery = Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) =>
        typeof value !== "undefined" &&
        value !== null &&
        String(value).length > 0
    )
  );

  return {
    startId: normalizedStartId.value,
    quizGuid: quizGuidForBookmark,
    title: bookmarkTitle.value,
    totalQuestions: totalQuestions.value,
    correctCount: correctCount.value,
    wrongCount: wrongCount.value,
    query: sanitizedQuery,
    requireReviewPin: requireReviewPin.value,
    key: null,
    route: {
      name: "QuizReviewPage",
      params: { startId: normalizedStartId.value },
      query: sanitizedQuery,
    },
  };
}

function normalizeReview(raw) {
  const list = raw?.questions || raw?.Questions || [];
  const normalized = list.map((q, idx) => {
    const optionsRaw = q.options || q.answer || q.Answers || [];
    // try to infer selected & correct ids
    const inferredSelected =
      q.selectedChoice ??
      optionsRaw.find((o) => o.isSelected)?.id ??
      optionsRaw.find((o) => o.isSelected)?.Id ??
      optionsRaw.find((o) => o.isSelected)?.Value ??
      null;

    const inferredCorrect =
      q.correctChoice ??
      optionsRaw.find((o) => o.isCorrect)?.id ??
      optionsRaw.find((o) => o.isCorrect)?.Id ??
      optionsRaw.find((o) => o.isCorrect)?.Value ??
      null;

    const options = optionsRaw.map((o) => ({
      id: o.id ?? o.Id ?? o.Value ?? o.value ?? o.choiceId,
      text: o.text ?? o.Answers ?? o.answer ?? o.label ?? String(o),
      isCorrect: Boolean(o.isCorrect),
      isSelected: Boolean(o.isSelected),
    }));

    return {
      questionId: q.questionId ?? q.QuestionId ?? q.id ?? q.number ?? idx + 1,
      number: q.number ?? idx + 1,
      question: q.question ?? q.Question ?? q.text ?? "",
      image: q.image || q.questionImagePath || q.Image || "",
      selectedChoice: inferredSelected,
      correctChoice: inferredCorrect,
      isCorrect:
        q.isCorrect ??
        (inferredSelected != null && inferredSelected === inferredCorrect),
      options,
    };
  });

  return {
    totalQuestions: raw?.totalQuestions ?? normalized.length,
    questions: normalized,
    quizTitle:
      raw?.quizTitle ??
      raw?.QuizTitle ??
      raw?.title ??
      raw?.Title ??
      raw?.quizName ??
      raw?.QuizName ??
      "",
    quizGuid:
      raw?.quizGuid ??
      raw?.QuizGuid ??
      raw?.guid ??
      raw?.Guid ??
      quizGuid ??
      null,
    wrongOnly:
      typeof raw?.wrongOnly !== "undefined"
        ? Boolean(raw?.wrongOnly)
        : undefined,
    completedAt:
      raw?.completedAt ??
      raw?.CompletedAt ??
      raw?.completedTime ??
      raw?.finishTime ??
      null,
  };
}

const isPinValidationError = (error) => {
  const status = error?.response?.status;
  const message = String(
    error?.response?.data?.message ||
      error?.response?.data?.Message ||
      error?.message ||
      ""
  ).toLowerCase();

  return (
    status === 401 ||
    status === 403 ||
    message.includes("pin") ||
    message.includes("key")
  );
};

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.message ||
  fallback;

async function verifyPinIfNeeded(pinCandidate) {
  if (!requireReviewPin.value || !quizGuid) {
    return { ok: true, pin: pinCandidate };
  }

  if (!pinCandidate) {
    pinError.value = "";
    showPinPrompt.value = true;
    return { ok: false };
  }

  try {
    const verification = parseVerificationResponse(
      await VerifyQuizReviewPin({ guid: quizGuid, pin: pinCandidate })
    );
    if (typeof verification?.requiresPin === "boolean") {
      requireReviewPin.value = verification.requiresPin;
    }

    if (!requireReviewPin.value || verification?.isValid) {
      showPinPrompt.value = false;
      return { ok: true, pin: pinCandidate };
    }

    pinError.value =
      verification?.message || "Invalid PIN. Please try again.";
    showPinPrompt.value = true;
    return { ok: false };
  } catch (error) {
    pinError.value = extractErrorMessage(
      error,
      "Unable to validate PIN. Please try again."
    );
    showPinPrompt.value = true;
    return { ok: false };
  }
}

async function fetchReview(pinOverride) {
  try {
    if (!startId) throw new Error("startId is required to fetch review");

    isLoading.value = true;
    fetchError.value = "";
    pinError.value = "";

    const wrongOnly =
      route.query.wrongOnly === "false" || route.query.wrongOnly === false;

    const candidate = sanitizePin(
      typeof pinOverride === "string" ? pinOverride : reviewKey.value
    );

    let pinForFetch = candidate;
    const verification = await verifyPinIfNeeded(candidate);
    if (!verification.ok) {
      return;
    }
    if (typeof verification.pin === "string") {
      pinForFetch = verification.pin;
    }

    const params = { startId, wrongOnly };
    if (pinForFetch) {
      params.Key = pinForFetch;
      if (pinForFetch !== reviewKey.value) {
        reviewKey.value = pinForFetch;
      }
    }

    const data = await GetQuizReview(params);
    review.value = normalizeReview(data);

    if (isBookmarked.value) {
      const updated = buildBookmarkPayload();
      if (updated) {
        bookmarkStore.updateBookmark(updated.startId, updated);
      }
    }

    showPinPrompt.value = false;

    const shouldUpdateKey =
      pinForFetch &&
      route.query.key !== pinForFetch &&
      route.query.Key !== pinForFetch;
    const shouldUpdatePinFlag =
      !requireReviewPin.value &&
      typeof route.query.requireReviewPin !== "undefined";
    if (shouldUpdateKey || shouldUpdatePinFlag) {
      const nextQuery = { ...route.query };
      delete nextQuery.Key;
      if (pinForFetch) {
        nextQuery.key = pinForFetch;
      }
      if (!requireReviewPin.value) {
        delete nextQuery.requireReviewPin;
      } else if (requireReviewPin.value) {
        nextQuery.requireReviewPin = "1";
      }
      router.replace({
        name: route.name || "QuizReviewPage",
        params: route.params,
        query: nextQuery,
      });
    }
  } catch (err) {
    console.error("Failed to fetch review:", err);
    review.value = { totalQuestions: 0, questions: [] };
    if (isPinValidationError(err)) {
      showPinPrompt.value = true;
      pinError.value = extractErrorMessage(
        err,
        "The PIN you entered is invalid. Please try again."
      );
    } else {
      fetchError.value = extractErrorMessage(
        err,
        "Failed to fetch review data."
      );
    }
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchReview);

function onSubmitPin() {
  const sanitized = sanitizePin(reviewKey.value);
  if (sanitized.length !== 4 || isLoading.value) return;
  reviewKey.value = sanitized;
  fetchReview(sanitized);
}

function closePinPrompt() {
  showPinPrompt.value = false;
  pinError.value = "";
  router.back()
}
</script>

<style scoped>
/* Keep the bullet perfectly round, independent of parent line-height or flex sizing */
.answer-bullet {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  aspect-ratio: 1 / 1;
  line-height: 0;
  -webkit-tap-highlight-color: transparent;
  background: #fff;
}
.answer-bullet--on {
  background: #2563eb;
  color: #fff;
}

/* Right-side status pill colors inherit from row */
div.border-green-500 > .ml-auto {
  color: #047857;
}
div.border-red-400 > .ml-auto {
  color: #b91c1c;
}
</style>
